import { Chip, Grid, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { ISelection } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/CustomTypeAhead';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectPlacementHeaderData, selectPlacementTags } from 'store/redux-store/placement-details/selectors';
import { AddTagsIcon } from './AddTagsIcon';
import lookupTags from 'app/assets/jsons/PlacementTags.json';
import { selectUser } from 'oidc/user.selectors';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import CloseIcon from '@mui/icons-material/Close';
import { IResponsiveChip, ResponsiveChips } from '@AMIEWEB/Common/ResponsiveChips/ResponsiveChips';
import { canManageOgpConfirmedStartTag, isCandidateContractLeadUser, userRoles } from 'oidc/userRoles';
import { useDecision } from '@optimizely/react-sdk';
import { ff_placement_ui_arbitration_agreement_grids } from 'app/constants/FeatureFlags/Global/keys';
import { Authorized } from 'oidc/userHelper';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { isDisableTLATagActivityStatus } from '@AMIEWEB/Placement/NextPlacementStatus/StatusDefaults';
import { CustomTooltip } from 'app/components/Common/Tooltips';

const useTagStyles = makeStyles()(theme => ({
  tooltipChip: {
    overflow: 'hidden',
    color: theme.palette.system.darkCharcoal,
    fontSize: '12px',
    background: theme.palette.system.shadedGray,
    textOverflow: 'ellipsis',
    '& .MuiChip-deleteIcon': {
      display: 'none',
    },
    '& #shortenedText': {
      display: 'none',
    },
    '&:hover': {
      color: `${theme.palette.system.white} !important`,
      background: theme.palette.system.lightBlue,
      '& .MuiChip-deleteIcon': {
        display: 'block',
      },
    },
  },
  container: {
    width: 'fit-content',
    overflow: 'hidden',
    msOverflowStyle: 'none',
  },
  closeIcon: {
    width: '15px',
    height: '15px',
  },
}));

export enum TagValues {
  TAXABLE = 'Taxable',
  WEEKLYPAY = 'Weekly Pay',
  PASSPORT = 'Passport',
  PRECHECK = 'Pre-Check',
  TLA = 'Traveler on Local Assignment',
  ONLINEMODULE = 'Online Module PID',
  OGP_CONFIRMED_START = 'OGP Confirmed Start',
}

/** US 496413: booking companies that may use the OGP Confirmed Start tag */
export const isOgpBookingCompanyDescription = (desc?: string | null) =>
  desc === "OGP-Int'l" || desc === 'OGP-Travel';

export const PlacementTagsWrapper = () => {
  const anchorAccessorId = 'tags-anchor-accessor';
  const dispatch = useDispatch();
  const { classes: tagClasses } = useTagStyles();
  const theme = useTheme();
    const { t } = useTranslation();

  const { tags, bookingCompanyDescription } = useSelector(selectPlacementTags);
  const placementDetails = useSelector(selectPlacementHeaderData);
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const user = useSelector(selectUser);
  const [arbitrationAgreementFlag] = useDecision(ff_placement_ui_arbitration_agreement_grids);

  const [tagsList, setTagsList] = useState<ISelection[]>([]);
  const [displayTags, setDisplayTags] = useState<IResponsiveChip[]>([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState<number>(null);

  const canDeleteTLA = (label: string) => {
    if (label !== TagValues.TLA) {
      return true;
    }

    return isDisableTLATagActivityStatus(currentPlacementStatus)
      ? false
      : isCandidateContractLeadUser(user.userInfo.roles);
  };

  const canDeleteOnlineModulePID = (label: string) => {
    if (label !== TagValues.ONLINEMODULE) {
      return true;
    }
    return Authorized(
      [userRoles.candidate_Contracts_Leadership, userRoles.candidate_Contracts_TeamMember],
      user.userInfo,
    );
  };

  const canDeleteOgpConfirmedStart = (label: string) => {
    if (label !== TagValues.OGP_CONFIRMED_START) {
      return true;
    }
    return canManageOgpConfirmedStartTag(user.userInfo.roles);
  };

  const canDelete = (label: string) => {
    return (
      label !== TagValues.TAXABLE &&
      label !== TagValues.WEEKLYPAY &&
      label !== TagValues.PASSPORT &&
      label !== TagValues.PRECHECK &&
      canDeleteTLA(label) &&
      canDeleteOnlineModulePID(label) &&
      canDeleteOgpConfirmedStart(label)
    );
  };

  const getTLATooltipMessage = (label: string): string | undefined => {
    if (label !== TagValues.TLA) {
      return undefined;
    }

    if (isDisableTLATagActivityStatus(currentPlacementStatus)) {
      return t('placement.profile.header.TLATagDisabledMessageForPlacementStatus');
    }

    if (!isCandidateContractLeadUser(user.userInfo.roles)) {
      return t('placement.profile.header.TLATagDisabledMessageForUserPermission');
    }

    return undefined;
  };

  const getChipTooltip = (label: string): string | undefined => {
    const tlaTip = getTLATooltipMessage(label);
    if (tlaTip) {
      return tlaTip;
    }
    if (label === TagValues.OGP_CONFIRMED_START && !canManageOgpConfirmedStartTag(user.userInfo.roles)) {
      return t('placement.profile.header.TLATagDisabledMessageForUserPermission');
    }
    return undefined;
  };

  const setupOptions = useMemo(() => {
    var updatedOptions = lookupTags.map(e => {
      return { ...e, name: e.label, key: e.id };
    });

    const forOgpBookingOnly = updatedOptions.filter(
      o => o.value !== 'OGPConfirmedStart' || isOgpBookingCompanyDescription(bookingCompanyDescription),
    );

    const optionsWithDisabledState = forOgpBookingOnly.map(option => {
      const tlaDisabled =
        !!currentPlacementStatus &&
        isDisableTLATagActivityStatus(currentPlacementStatus) &&
        option.label === TagValues.TLA;
      const ogpDisabled =
        option.label === TagValues.OGP_CONFIRMED_START &&
        !canManageOgpConfirmedStartTag(user.userInfo.roles);
      const isDisabled = tlaDisabled || ogpDisabled;
      let disabledTooltip: string | undefined;
      if (tlaDisabled) {
        disabledTooltip = getTLATooltipMessage(option.label);
      } else if (ogpDisabled) {
        disabledTooltip = t('placement.profile.header.TLATagDisabledMessageForUserPermission');
      }
      return {
        ...option,
        isDisabled,
        disabledTooltip,
      };
    });

    setOptions(optionsWithDisabledState);
    const filteredOptions = [];
    optionsWithDisabledState
      ?.filter(o => !o.readOnly)
      .forEach(option => {
        if (option.roles.length > 0) {
          if (option.roles.some(r => user?.userInfo?.roles?.includes(r))) {
            filteredOptions.push(option);
          }
        } else {
          filteredOptions.push(option);
        }
      });
    filteredOptions.sort((a, b) => a.label.localeCompare(b.label));
    setTagsOptions(filteredOptions);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTags, currentPlacementStatus, bookingCompanyDescription, user?.userInfo?.roles]);

  useEffect(() => {
    if (tags) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      setupOptions;

      const filterTags = options
        .filter(lookupTagItem => tags.find(tag => tag.value === lookupTagItem.value))
        .map(e => {
          return { ...e, name: e.label };
        });
      const fDisplayTags: IResponsiveChip[] = filterTags?.map(option => ({
        id: option.id,
        title: option.label,
        isVisible: true,
        canDelete: canDelete(option.label),
        tooltip: getChipTooltip(option.label),
      }));
      setTagsList(filterTags);
      setDisplayTags(fDisplayTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, options, currentPlacementStatus]);

  const handleDeleteTag = filteredTags => {
    const updatedTags = {
      tags: filteredTags,
      placementFlagTypeId: selectedTagId,
    };
    const postReqBody = {
      placementId: placementDetails?.placementId,
      updatedBy: user.userInfo.employeeId,
      placementTags: updatedTags,
      arbitrationAgreementFlagEnabled: arbitrationAgreementFlag?.enabled,
    };
    dispatch(placementDetailsAction.updatePlacementTags(postReqBody));
  };

  const handleChipDelete = (tagItem: IResponsiveChip, index: number) => {
    const filteredTags = tagsList?.filter(currentItem => currentItem.id !== tagItem.id);
    handleDeleteTag(filteredTags);
    setDisplayTags(displayTags.filter(chip => chip.id !== tagItem.id));
  };

  const tooltipContent = (remainingChips: IResponsiveChip[]) => {
    return (
      <Grid container direction="column" style={{ flex: 1, overflow: 'auto' }}>
        {remainingChips?.map((item, index) => {
          return (
            <Grid item key={index}>
              <TooltipChip item={item} index={index} />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const TooltipChip = ({ item, index }: { item: IResponsiveChip; index: number }) => {
    const [maxWidth, setMaxWidth] = React.useState(undefined);
    const itemRef = useRef() as MutableRefObject<HTMLDivElement>;

    useEffect(() => {
      setMaxWidth(itemRef?.current?.scrollWidth + 1);
    }, [itemRef?.current]);

    const chipElement = (
      <Chip
        id={`tags-placement-header-${index}`}
        className={tagClasses.tooltipChip}
        label={item.title}
        onDelete={canDelete(item.title) ? () => handleChipDelete(item, index) : undefined}
        deleteIcon={
          canDelete(item.title) ? (
            <CloseIcon
              className={tagClasses.closeIcon}
              style={{ color: theme.palette.components.icons.primary.color }}
            />
          ) : undefined
        }
        sx={{ maxWidth: maxWidth }}
      />
    );

    return (
      <div
        ref={itemRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          margin: theme.spacing(0.5),
        }}
      >
        {item.tooltip ? (
          <CustomTooltip
            style={{ color: theme.palette.system.neutralGray, top: 5 }}
            tooltipContent={item.tooltip}
            placement="bottom-start"
            isInlineContent={true}
            tooltipPlacementBottom={true}
            contentStyle={{ fontWeight: 'normal' }}
          >
            {chipElement}
          </CustomTooltip>
        ) : (
          chipElement
        )}
      </div>
    );
  };

  return (
    <>
      <ResponsiveChips
        key={displayTags.length}
        chips={displayTags}
        onDelete={handleChipDelete}
        overflowTooltip={true}
        tooltipMaxWidth={500}
        overflowTooltipRenderer={tooltipContent}
        addChip={tagsOptions ? true : false}
        addChipRenderer={
          <AddTagsIcon
            anchorAccessorId={anchorAccessorId}
            tagsOptions={tagsOptions}
            tagsList={tagsList}
            selectedTagId={selectedTagId}
            setSelectedTagId={setSelectedTagId}
          />
        }
        isSortable={true}
        numRows={2}
      />
    </>
  );
};
